import React from 'react';
import { Navigate, useLocation } from 'react-router';
import { Box, CircularProgress } from '@mui/material';
import { useSession } from 'src/SessionContext';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredRole?: string;
}

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children, requiredRole }) => {
  const { session, loading } = useSession(); // Get session and loading state
  const location = useLocation();

  // Show loading indicator while the session is being verified
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // After loading, check if user is authenticated
  if (!session?.user) {
    // Pass the current location in state so SignIn can redirect back
    return <Navigate to={`/sign-in`} state={{ from: location }} replace />;
  }

  // User is authenticated, check role if required
  if (requiredRole && session.accountType !== requiredRole) {
    // Redirect to the appropriate homepage based on their actual role
    if (session.accountType === 'Staff') {
      return <Navigate to="/staff" replace />;
    } else if (session.accountType === 'Talent') {
      return <Navigate to="/dashboard" replace />;
    } else if (session.accountType === 'Client') {
      return <Navigate to="/client" replace />;
    } else {
      // Fallback if accountType is unknown/invalid (should ideally not happen)
      return <Navigate to="/no-access" replace />;
    }
  }

  // User is authenticated and has the required role (or no role required)
  return <>{children}</>;
};
